/**
 * The declared-lists register (FLOW-243) — what a business tells us about itself.
 *
 * One shape, five kinds, because a declared account, a declared funder, a
 * related party and a director are the same act: the client stating a fact
 * about itself, attributably and at a moment in time.
 *
 * 🚨 **`declared_source` and `declaration_note` are deliberately absent.** They
 * are Ops-internal (N4) — whether Ops added a row the client never declared is
 * the investigation's own signal. The Ops surfaces read them through their own
 * endpoint; a client screen must never render them.
 */

export type DeclarationKind =
  'ACCOUNT' | 'ACCOUNT_AFFIRMATION' | 'FUNDER' | 'RELATED_PARTY' | 'DIRECTOR';

type DeclarationStatus = 'DECLARED' | 'WITHDRAWN';

/**
 * The four-state answer, and it must never collapse to a boolean: an
 * unanswered question stored as "no" reads as the client declaring they have
 * none. *Nothing selected* is the empty string.
 */
export type FourState = '' | 'YES' | 'NO' | 'NOT_SURE';

/** The funder question's four-state — same shape, different words. */
type FacilityState = '' | 'LIVE' | 'SETTLED' | 'NOT_SURE';

type FacilityType = '' | 'DEBTOR_FINANCE' | 'TERM_LOAN' | 'OVERDRAFT' | 'ASSET_FINANCE' | 'OTHER';

type PartyRelationship = '' | 'HOLDING' | 'SUBSIDIARY' | 'COMMON_DIRECTOR' | 'FAMILY' | 'OTHER';

type DirectorRole = '' | 'DIRECTOR' | 'SHAREHOLDER' | 'BOTH';

/**
 * Whose account this is (FC-DET-047 as extended 2026-09-22). `''` = not asked.
 * 🚨 Only `BUSINESS` is an account the client *operates* — the one kind that
 * enters the list an own-account transfer is proved against. A director's or a
 * related business's account is a related-party fact, not one of ours.
 */
export type HolderRelationship =
  '' | 'BUSINESS' | 'DIRECTOR_OR_SHAREHOLDER' | 'RELATED_BUSINESS' | 'OTHER';

export const HOLDER_RELATIONSHIP_LABELS: Record<Exclude<HolderRelationship, ''>, string> = {
  BUSINESS: 'This business',
  DIRECTOR_OR_SHAREHOLDER: 'A director or shareholder',
  RELATED_BUSINESS: 'A related business',
  OTHER: 'Someone else',
};

/** The declared account as the business reads it back — the tail, not the number. */
export interface DeclaredBankAccount {
  short_id: string;
  bank_name: string;
  branch_code: string;
  account_number_tail: string;
  account_holder_name: string;
}

/**
 * One declaration. Keys outside the row's own kind are absent, not blank — a
 * funder row carrying an empty `full_name` would invite a reader to conclude
 * the director was not captured, when it was never asked.
 */
export interface Declaration {
  short_id: string;
  kind: DeclarationKind;
  status: DeclarationStatus;
  declared_at: string;
  declared_by_name: string;
  /** short_id of the row this one withdraws (set only on a WITHDRAWN row). */
  withdraws: string | null;
  /**
   * DECLARED *and nothing has withdrawn it*. 🚨 Key every "is this live?"
   * decision on this and never on `status`: a withdrawn row keeps
   * `status: 'DECLARED'` under the append-only rule, so a screen reading the
   * status would show a retracted declaration as current forever.
   */
  is_in_force: boolean;

  // kind=ACCOUNT
  bank_account?: DeclaredBankAccount;
  holder_name?: string;
  carries_trading_activity?: FourState;
  /** `null` means the question was not put — NOT "this is not the main account". */
  is_principal_trading_account?: boolean | null;
  /**
   * The system read this account off an uploaded statement (FC-DET-047) —
   * the client is shown it to confirm or correct, never to re-type.
   */
  derived?: boolean;
  holder_relationship?: HolderRelationship;
  /** short_id of the DIRECTOR / RELATED_PARTY declaration the holder is. */
  holder_party?: string | null;
  /** An account operated but not uploaded — the client's reason, in its own words. */
  not_uploaded_reason?: string;
  /** short_id of the derived row this one corrects (a person's override). */
  corrects?: string | null;

  // kind=FUNDER
  funder_name?: string;
  facility_type?: FacilityType;
  facility_state?: FacilityState;

  // kind=RELATED_PARTY
  party_name?: string;
  relationship?: PartyRelationship;

  // kind=FUNDER and kind=RELATED_PARTY
  payer_names?: string[];

  // kind=DIRECTOR
  full_name?: string;
  /**
   * Another person's identity document. Present for the business's own members
   * and for Ops; **absent** (never blank) for an agent acting for the business.
   */
  id_number?: string;
  director_role?: DirectorRole;
  appointed_from?: string | null;

  // Ops-only reads (never on a client surface, N4)
  declared_source?: 'CLIENT_DECLARED' | 'OPS_ADDED' | 'DERIVED_FROM_STATEMENT' | 'CIPC';
  declaration_note?: string;
  bank_account_number?: string | null;

  /**
   * Only ever on the response to a WRITE, and only when the write changed
   * nothing: the account was already on the list, so the server appended no row
   * and handed back the one already standing (200, not 201). Never present on a
   * row read back from the list — a screen must not key any "is this live?"
   * decision on it.
   */
  already_declared?: boolean;
}

export interface DeclarationsResponse {
  declarations: Declaration[];
}

interface DeclareAccountPayload {
  kind: 'ACCOUNT';
  bank_name: string;
  /** Optional: the number identifies the account within the business. */
  branch_code?: string;
  account_number: string;
  account_holder_name: string;
  holder_name?: string;
  carries_trading_activity?: FourState;
  is_principal_trading_account?: boolean | null;
  holder_relationship?: HolderRelationship;
  holder_party?: string | null;
  not_uploaded_reason?: string;
}

/**
 * A person's correction of a derived account. Two shapes on one door: the
 * identity re-typed (all three of bank / number / holder, or none) and/or the
 * answers on the row. Absent keeps what the derived row has.
 */
export interface CorrectAccountPayload {
  bank_name?: string;
  branch_code?: string;
  account_number?: string;
  account_holder_name?: string;
  holder_relationship?: HolderRelationship;
  holder_party?: string | null;
  not_uploaded_reason?: string;
}

interface DeclareFunderPayload {
  kind: 'FUNDER';
  funder_name: string;
  facility_type?: FacilityType;
  facility_state?: FacilityState;
  payer_names?: string[];
}

interface DeclareRelatedPartyPayload {
  kind: 'RELATED_PARTY';
  party_name: string;
  relationship?: PartyRelationship;
  payer_names?: string[];
}

interface DeclareDirectorPayload {
  kind: 'DIRECTOR';
  full_name: string;
  id_number?: string;
  director_role?: DirectorRole;
  appointed_from?: string | null;
}

interface DeclareAffirmationPayload {
  kind: 'ACCOUNT_AFFIRMATION';
}

export type DeclarePayload =
  | DeclareAccountPayload
  | DeclareFunderPayload
  | DeclareRelatedPartyPayload
  | DeclareDirectorPayload
  | DeclareAffirmationPayload;
